import type { ApiErrorResponse } from '../types/api';
import { getCurrentFrontendAccessToken } from '@nwm-oss/auth-react/token-accessor';
import { notifyAuthenticationRequired } from '@nwm-oss/auth-react/authentication-required';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/integration/v1';
const GET_CACHE_TTL_MS = 5_000;
const getCache = new Map<string, { expiresAt: number; promise: Promise<unknown> }>();
let cacheToken: string | null | undefined;

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload?: ApiErrorResponse,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const token = getCurrentFrontendAccessToken();
  if (token !== cacheToken) {
    getCache.clear();
    cacheToken = token;
  }
  if (method !== 'GET') getCache.clear();
  const cached = method === 'GET' ? getCache.get(path) : undefined;
  if (cached && cached.expiresAt > Date.now()) return cached.promise as Promise<T>;

  const request = performApiRequest<T>(path, init);
  if (method === 'GET') {
    getCache.set(path, { expiresAt: Date.now() + GET_CACHE_TTL_MS, promise: request });
    request.catch(() => getCache.delete(path));
  }
  return request;
}

async function performApiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = buildHeaders(init);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    notifyAuthenticationRequired(response.status);
    let payload: ApiErrorResponse | undefined;

    try {
      payload = (await response.json()) as ApiErrorResponse;
    } catch {
      payload = undefined;
    }

    throw new ApiClientError(payload?.message ?? `Request failed with status ${response.status}`, response.status, payload);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/plain')) {
    return (await response.text()) as T;
  }

  return (await response.json()) as T;
}

export interface ApiBlobResponse {
  blob: Blob;
  filename?: string;
}

export async function apiBlobRequest(path: string, init?: RequestInit): Promise<ApiBlobResponse> {
  const headers = buildHeaders(init);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    notifyAuthenticationRequired(response.status);
    let payload: ApiErrorResponse | undefined;

    try {
      payload = (await response.json()) as ApiErrorResponse;
    } catch {
      payload = undefined;
    }

    throw new ApiClientError(payload?.message ?? `Request failed with status ${response.status}`, response.status, payload);
  }

  return {
    blob: await response.blob(),
    filename: readFilename(response.headers.get('content-disposition')),
  };
}

export { API_BASE_URL };

function buildHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);
  const token = getCurrentFrontendAccessToken();

  if (init?.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (!headers.has('Authorization')) {
    throw new ApiClientError('Authentication required. Sign in with Keycloak/OIDC before calling OSS Integration APIs.', 401, {
      code: 'AUTH_REQUIRED',
      message: 'Authentication required. Sign in with Keycloak/OIDC before calling OSS Integration APIs.',
      details: [],
      traceId: '',
    });
  }

  return headers;
}

function readFilename(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) {
    return undefined;
  }

  const match = /filename="([^"]+)"/i.exec(contentDisposition) ?? /filename=([^;]+)/i.exec(contentDisposition);
  return match?.[1]?.trim();
}

